import {
  createArtworks,
  createUploadUrl,
  finalizeUploadArtwork,
  createCallsheets,
  updateCallsheet,
  removeArtworkToFile,
  removeCallsheet,
  BestDownloadFilmFile,
  finalizeUploadViLabel,
  updateFilmToPerson,
  createFilmToPerson,
  removeFilmToPerson,
  updateCallsheetAnalysis,
  createPerson,
  createCallsheetAnalysis
} from '@/gql'
import filesize from 'filesize'

// this bounces a film from a download link to gcs storage
export const bounceFilm = ({ apolloClient, filmMasterID }) => {

  return apolloClient.query({
    query: BestDownloadFilmFile,
    variables: {
      id: filmMasterID,
      // attempt to get the source
      source: true
    },
    fetchPolicy: 'no-cache'
  }).then(({ data }) => {
    // this actually returns a film master - so we need to go further into it
    const filmFile = data && data.BestDownloadFilmFile && data.BestDownloadFilmFile.BestDownloadFilmFile;
    const filmDownload = filmFile && filmFile.FilmFileDownload;
    const artifact = filmFile && filmFile.FilmFileArtifact;
    const { patchedLink: downloadLink, mimeType } = filmDownload || {};
    const pack = {
      filmFile,
      filmDownload,
      filmMasterID
    }

    // give up if we didnt get necessaey base info
    if (!downloadLink || !mimeType) {
      return {
        pack: {
          ...pack,
          error: 'no downloadable file or missing mimetype'
        }
      }
    }

    // we already have one on storage, no need to repeat
    if(artifact && artifact.size === filmFile.size) {
      return {
        pack: {
          ...pack,
          gcsUrl: artifact.name
        }
      }
    }

    // make sure thats okay
    if(!confirm(`Download film source using your network connection (${filesize(filmFile.size)}) ?`)) {
      return {
        pack: {
          ...pack,
          error: 'download refused - cannot proceed'
        }
      }
    }

    // starting download
    return fetch(downloadLink).then(response => {
      pack.status = response.status
      if (!response.ok) {
        return {
          pack: {
            ...pack,
            message: response.statusMessage,
            error: 'failed downloading file'
          }
        }
      } else {
        return response.blob()
          .then(blob => ({
            pack,
            blob
          }))
      }
    }).then(({pack, blob}) => {
      if (pack.error) return {
        pack
      }
      if (!blob) {
        pack.error = 'no data found for file'
      } else if (blob.size !== filmFile.size) {
        pack.message =  pack.error = `expected size ${filmFile.size} but got ${blob.size}`
      }
      if (pack.error) return {
        pack
      }
      // let this one pass
      if (blob.type !== mimeType) {
        console.log(`expected type ${mimeType} but got ${blob.type}`)
      }

      // now need to get an upload url
      return createUploadUrlGql({ contentType: blob.type, apolloClient }).then(upload => {
        return {
          pack,
          blob,
          upload: upload.data
        }
      })
    }).then(({ pack, blob, upload }) => {
      if (pack.error) return { pack };

      // get a storage temporary upload id
      const { url } = upload.createUploadUrl.SignedUrl
      const { uploadID } = upload.createUploadUrl
      return storagePutBlob({ url, blob })
        .then(() => {
          return {
            pack,
            uploadID
          }
        })

    }).then(({ pack, uploadID }) => {
      // finalize the upload
      console.log('upload done', new Date().getTime());
      if (pack.error) {
        return { ...pack};
      }
      return apolloClient.mutate({
        mutation: finalizeUploadViLabel,
        variables: {
          uploadID,
          customName: pack.filmFile.md5
        },
        fetchPolicy: 'no-cache'
      }).then(({data}) => {
        const r = data && data.finalizeUploadViLabel;
        console.log('all over',r, new Date().getTime())
        return {
          ...pack,
          ...r
        }
      });
    }).catch(error => {
      return {
        ...pack,
        error,
        message: 'Something went wrong - please try again'
      }
    });

  })

}

export const storagePutBlob = ({url, blob}) => {

  var headers = new Headers({
    'Content-Type': blob.type,
  });

  return fetch(url, {
    method: 'PUT',
    body: blob,
    headers,
  });
};

export const storagePut = ({uploadUrl, file}) => {

  var headers = new Headers({
    'Content-Type': file.file.type,
  });

  return fetch(uploadUrl.SignedUrl.url, {
    method: 'PUT',
    body: file.file,
    headers,
  });
};

export const updateCallsheetAnalysisGql = ({apolloClient, data}) => {
  return apolloClient
    .mutate({
      mutation: updateCallsheetAnalysis,
      variables: {
        id: data.id,
        input: data
      },
    })
    .then(result => result.data.updateCallsheetAnalysis);
};

export const updateFilmToPersonGql = ({apolloClient, data}) => {
  return apolloClient
    .mutate({
      mutation: updateFilmToPerson,
      variables: {
        id: data.id,
        input: data
      },
    })
    .then(result => result.data.updateFilmToPerson);
};

export const removeFilmToPersonGql = ({apolloClient, id}) => {
  return apolloClient
    .mutate({
      mutation: removeFilmToPerson,
      variables: {
        id
      },
    })
    .then(result => result.data.removeFilmToPerson);
};

export const createFilmToPersonGql = ({apolloClient, data}) => {
  return apolloClient
    .mutate({
      mutation: createFilmToPerson,
      variables: {
        input: data
      },
    })
    .then(result => result.data.createFilmToPerson);
};

const creator = ({apolloClient, input, mutation}) => {
  return apolloClient
    .mutate({
      mutation,
      variables: {
        input
      },
    })
    .then(result => result.data[Object.keys(result.data)[0]]);
};

export const createPersonGql = ({apolloClient, data}) => creator({
  mutation: createPerson,
  input: data,
  apolloClient
})

export const createCallsheetAnalysisGql = ({apolloClient, data}) => creator({
  mutation: createCallsheetAnalysis,
  input: data,
  apolloClient
})


export const updateCallsheetGql = ({apolloClient, callsheet}) => {
  return apolloClient
    .mutate({
      mutation: updateCallsheet,
      variables: {
        id: callsheet.id,
        input: {
          originalName: callsheet.originalName,
          comment: callsheet.comment,
        },
      },
    })
    .then(result => result.data.updateCallsheet);
};
// need to remove the call sheet and all its artwork
// but cannot rely on cascading from artwork level, since the files from storage need removing too
export const removeCallsheetAndArtworkGql = ({apolloClient, callsheet}) => {
  // first delete the artwork to file which also deletes the artifacts
  return Promise.all(
    callsheet.Artwork.ArtworkToFiles.map(f =>
      apolloClient.mutate({
        mutation: removeArtworkToFile,
        variables: {
          id: f.id,
        },
      })
    )
  ).then(() => {
    // now we can delete the call sheet and allow the cascade to the artwork
    return apolloClient.mutate({
      mutation: removeCallsheet,
      variables: {
        id: callsheet.id,
      },
    });
  });
};

export const createCallsheetsGql = ({files, apolloClient, filmMasterID, runID}) => {
  return apolloClient
    .mutate({
      mutation: createCallsheets,
      variables: {
        input: {
          rows: files.map(file => ({
            filmMasterID,
            runID,
            artworkID: file.artworkID,
            comment: 'callsheet',
            originalName: `${file.file.name}`,
          })),
        },
      },
    })
    .then(result => result.data.createCallsheets);
};
export const finalizeUploadArtworkGql = ({
  input,
  uploadID,
  customName,
  apolloClient,
}) => {
  return apolloClient
    .mutate({
      mutation: finalizeUploadArtwork,
      variables: {
        input,
        uploadID,
        customName,
      },
    })
    .then(result => result.data.finalizeUploadArtwork);
};

export const createArtworksGql = ({
  files,
  apolloClient,
  artworkTypeID,
  runID,
  comment,
}) => {
  return apolloClient
    .mutate({
      mutation: createArtworks,
      variables: {
        input: {
          rows: files.map(file => ({
            comment: `${file.file.file.name}: ${comment}`,
            artworkTypeID,
            runID,
          })),
        },
      },
    })
    .then(result => result.data.createArtworks);
};

// get a an upload url for storage upload
export const createUploadUrlGql = ({ contentType, apolloClient }) =>
  apolloClient.mutate({
    mutation: createUploadUrl,
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        contentType
      }
    }
  });

// get a collection of upload urls for a bunch of files
export const createArtworkUploadUrlsGql = ({ files, apolloClient }) =>
  Promise.all(files.map(f => createUploadUrlGql({ contentType: f.file.type, apolloClient })))
    .then(results =>
      results.map((f, i) => ({
        uploadUrl: f.data.createUploadUrl,
        index: i,
        file: files[i],
      })));


// not needed for now
export const serializePromises = ({promises, all}) => {
  return all ? // this returns all the results
      promises.reduce(
        (p, c) => p.then(a => c().then(r => a.push(r) && a)),
        Promise.resolve([])
      )
    : // this returns a single result
      promises.reduce((p, c) => p.then(() => c()), Promise.resolve());
};
